import { useState } from "react";
import { useWaves } from "../context/WaveContext";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Trash2, CheckCircle2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Waves() {
  const { waves, currentWaveId, setCurrentWaveId, refresh } = useWaves();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", month_from: "", month_to: "", clone_from: "" });

  const setCurrent = async (wid) => {
    await api.post(`/waves/${wid}/set-current`);
    setCurrentWaveId(wid);
    toast.success("Wave activada");
    await refresh();
  };

  const remove = async (w) => {
    if (!window.confirm(`¿Eliminar ${w.name} y TODOS sus datos (restaurantes, eventos, facturas)?`)) return;
    try {
      await api.delete(`/waves/${w.id}`);
      toast.success("Wave eliminada");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const create = async () => {
    if (!form.name) return toast.error("Nombre obligatorio");
    if (form.month_from && form.month_to && form.month_from > form.month_to) {
      return toast.error("El mes 'Desde' debe ser anterior o igual al mes 'Hasta'");
    }
    try {
      const payload = {
        name: form.name,
        month_from: form.month_from || null,
        month_to: form.month_to || null,
        clone_from: form.clone_from || null,
      };
      const { data } = await api.post("/waves", payload);
      toast.success(form.clone_from ? "Wave creada con restaurantes copiados" : "Wave creada");
      setOpen(false); setForm({ name: "", month_from: "", month_to: "", clone_from: "" });
      await refresh();
      setCurrentWaveId(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="waves-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Periodos</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Waves</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">Cada wave es un periodo (ej: Wave 2 2026 = los próximos 3 meses). Toda la información (restaurantes, eventos, facturas) está separada por wave. Cambia de wave con el selector arriba a la derecha.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-wave-button" className="rounded-none bg-zinc-900 hover:bg-zinc-800">
              <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nueva wave
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Nueva wave</DialogTitle>
              <DialogDescription>Crea un periodo nuevo de trabajo. Toda la información (restaurantes, eventos, facturas, empleados) será independiente de las demás waves.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs uppercase tracking-wider">Nombre</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" placeholder="Wave 3 2026" data-testid="wave-name-input" />
              </div>
              <div className="border-t border-zinc-100 pt-3">
                <p className="label-eyebrow text-zinc-500 mb-2">Periodo de la wave (opcional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Desde mes</Label>
                    <Input
                      type="month"
                      value={form.month_from}
                      onChange={(e) => setForm({ ...form, month_from: e.target.value })}
                      className="rounded-none"
                      data-testid="wave-month-from"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Hasta mes</Label>
                    <Input
                      type="month"
                      value={form.month_to}
                      onChange={(e) => setForm({ ...form, month_to: e.target.value })}
                      className="rounded-none"
                      data-testid="wave-month-to"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">El calendario de Eventos abrirá en el mes &quot;Desde&quot; y la navegación quedará limitada a este rango.</p>
              </div>
              <div className="border-t border-zinc-100 pt-3">
                <p className="label-eyebrow text-zinc-500 mb-2">Copiar restaurantes (opcional)</p>
                <Label className="text-xs uppercase tracking-wider">Desde wave</Label>
                <Select value={form.clone_from || "none"} onValueChange={(v) => setForm({ ...form, clone_from: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-none" data-testid="wave-clone-from">
                    <SelectValue placeholder="No copiar (empezar vacío)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No copiar (empezar vacío)</SelectItem>
                    {waves.map((w) => (
                      <SelectItem key={w.id} value={w.id} data-testid={`wave-clone-option-${w.code}`}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-zinc-500 mt-1">Se copiarán solo los restaurantes (precio, IVA, capacidades, turnos). No se copian eventos ni facturas.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="wave-create-confirm">Crear y abrir</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
          <div className="col-span-4">Nombre</div>
          <div className="col-span-2">Código</div>
          <div className="col-span-2">Activa global</div>
          <div className="col-span-2">Seleccionada</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        {waves.length === 0 && <div className="p-10 text-center text-sm text-zinc-500">Sin waves.</div>}
        {waves.map((w) => (
          <div key={w.id} className="grid grid-cols-12 px-6 py-4 border-b border-zinc-100 items-center text-sm" data-testid={`wave-row-${w.code}`}>
            <div className="col-span-4 font-medium">{w.name}</div>
            <div className="col-span-2 font-mono text-zinc-600">{w.code}</div>
            <div className="col-span-2">
              {w.is_current ? (
                <span className="inline-flex items-center text-xs px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-700 uppercase tracking-wider">
                  <CheckCircle2 className="h-3 w-3 mr-1" strokeWidth={1.5} /> Activa
                </span>
              ) : (
                <button onClick={() => setCurrent(w.id)} data-testid={`activate-${w.code}`} className="text-xs uppercase tracking-wider text-zinc-600 hover:text-zinc-900 hover:underline">Activar global</button>
              )}
            </div>
            <div className="col-span-2">
              {w.id === currentWaveId ? (
                <span className="text-xs uppercase tracking-wider text-zinc-900 font-semibold">Viendo</span>
              ) : (
                <button onClick={() => setCurrentWaveId(w.id)} data-testid={`select-${w.code}`} className="text-xs uppercase tracking-wider text-zinc-600 hover:text-zinc-900 hover:underline">Ver</button>
              )}
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button onClick={() => remove(w)} data-testid={`delete-${w.code}`} disabled={waves.length <= 1} className="text-zinc-600 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed">
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
